/* eslint-env browser */
import { useEffect } from 'react';

/**
 * Custom boot animation for Business Coach
 *
 * 🔴 EVERY CLASS NAME HERE IS `bc-` PREFIXED, AND THAT IS LOAD-BEARING — DO NOT
 * "TIDY" IT BACK TO THE SHORTER NAMES. localAppShim.js imports EVERY app's
 * BootAnim eagerly (they are static imports), and each module injects its
 * <style> tag as a MODULE-EVAL SIDE EFFECT — so every app's stylesheet is in the
 * document regardless of which BootAnim actually renders. With shared class
 * names they are all the same selector at the same specificity, and the LAST one
 * inserted wins.
 *
 * Measured 2026-08-27 on /a/business-coach/?bundleAppId=com.johnoberg.app: the
 * injected order was matm -> precina -> asc -> admin-app DEFAULT, so the
 * default's `.boot-anim-spinner { color: rgba(187,83,163,.5) }` (pink) overrode
 * BC's gold. BC's navy background survived only because the default's
 * `.boot-anim-wrapper` has its `background` COMMENTED OUT — which is what made
 * this look like "a different pink spinner component" rather than what it was:
 * BC's own boot anim wearing another app's colour.
 * Uses navy (#0D2838) + golden (#FAC348) colors to match the app's branding
 * This component is rendered by index.js before App.js loads TW or anything else
 */

const stylesheet = `
	@keyframes bc-boot-anim-spin {
		to {
			transform: rotate(360deg);
		}
	}

	.bc-boot-anim-wrapper {
		position: fixed;
		top: 0;
		left: 0;
		bottom: 0;
		right: 0;
		background: linear-gradient(to bottom right, #0D2838, #09141c);
	}

	.bc-boot-anim-spin-position {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}

	.bc-boot-anim-spinner {
		animation: bc-boot-anim-spin 1s linear infinite;
		width: 8rem;
		height: 8rem;
		color: rgba(250, 195, 72, 0.5); /* #FAC348 golden accent with opacity */
	}
`;

// I know it's generally bad form in react to modify the DOM,
// but I'm doing this here to prevent FOC on first boot.
// We still remove this from the DOM as soon as we unmount, so pretty safe.
const style = document.createElement('style');
style.id = 'boot-anim-bc';
style.innerHTML = stylesheet;
const insertLocation = document.querySelector('script');
insertLocation.parentNode.insertBefore(style, insertLocation);

export function BootAnim() {
	// 🔴 THE `[]` IS LOAD-BEARING — DO NOT DROP IT. Without a dep array this
	// cleanup runs after EVERY render, removing the <style> tag that is injected
	// only once at module eval; nothing re-inserts it.
	//
	// This was latent before the bc- rename ONLY BY ACCIDENT: the markup used to
	// carry the shared class names, so even with BC's own tag gone the admin-app
	// default's still-resident `.boot-anim-*` rules kept the spinner positioned,
	// sized and animating (merely pink). Namespacing removed that safety net, so
	// a single re-render — a future state change, StrictMode's double-invoke, or
	// any repeat root.render(<BootAnim/>) — would now leave an unstyled,
	// unpositioned, unanimated SVG on a white page.
	useEffect(() => {
		return () => {
			if (insertLocation && insertLocation.parentNode) {
				try {
					insertLocation.parentNode.removeChild(style);
				} catch (error) {
					// eslint-disable-next-line no-console
					console.warn(`[BC BootAnim] Error removing style element:`, error);
				}
			}
		};
	}, []);

	return (
		<div className="bc-boot-anim-wrapper">
			<div className="bc-boot-anim-spin-position">
				<svg
					className="bc-boot-anim-spinner"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
				>
					{/* 🔴 A PRESENTATION ATTRIBUTE, NOT `className="opacity-25"`.
					    This component renders BEFORE Tailwind loads (see the header),
					    and the injected stylesheet defines no `.opacity-25` rule — so
					    the Tailwind class did nothing and the track ring painted at
					    FULL opacity. The very next spinner
					    (components/FullPageSpinner.js) uses `opacity="0.25"` as an
					    attribute and does dim, so the ring visibly darkened at the
					    handoff: the same "two different spinners" pop this whole
					    change exists to remove, one layer down. */}
					<circle
						opacity="0.25"
						cx="12"
						cy="12"
						r="10"
						stroke="currentColor"
						strokeWidth="4"
					></circle>
					<path
						fill="currentColor"
						d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
					></path>
				</svg>
			</div>
		</div>
	);
}
